/**
 * Three live visual studies for the combined wind instrument.
 * The host interpolates readings and calls updateWindDesign on each frame.
 * Angles are degrees, clockwise from the bow; speeds are knots.
 */
export const windDesignNames = Object.freeze({
  halo: "Halo",
  sail: "Sail",
  tactical: "Tactical",
});

const C = {
  coral: "#d9775f",
  white: "#f6f6fa",
  muted: "#a0a0a8",
  quiet: "#69696f",
  line: "#38383d",
  dark: "#171719",
};

const finite = (value, fallback = 0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback;
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const angle = (n) => ((finite(n) % 360) + 360) % 360;
const relativeAngle = (n) => ((angle(n) + 180) % 360) - 180;
const precision = (n) => finite(n).toFixed(3);
const speed = (n) => clamp(finite(n), 0, 999).toFixed(1);
const heading = (n) => String(Math.round(angle(n)) % 360).padStart(3, "0");
const signed = (n) => {
  const a = Math.round(relativeAngle(n));
  return `${Math.abs(a)}° ${a < 0 ? "P" : a > 0 ? "S" : ""}`.trim();
};
const point = (cx, cy, r, a) => {
  const rad = (finite(a) * Math.PI) / 180;
  return [cx + Math.sin(rad) * r, cy - Math.cos(rad) * r];
};
const xy = (p) => p.map(precision).join(" ");

function arc(cx, cy, r, start, end) {
  const span = finite(end) - finite(start);
  if (Math.abs(span) < 0.001) return "";
  const steps = Math.ceil(Math.abs(span) / 90);
  let d = `M ${xy(point(cx, cy, r, start))}`;
  for (let i = 1; i <= steps; i += 1) {
    const p = point(cx, cy, r, start + (span * i) / steps);
    d += ` A ${precision(r)} ${precision(r)} 0 0 ${span > 0 ? 1 : 0} ${xy(p)}`;
  }
  return d;
}

function sector(cx, cy, inner, outer, center, spread) {
  const start = center - spread;
  const end = center + spread;
  return `M ${xy(point(cx, cy, outer, start))} A ${outer} ${outer} 0 0 1 ${xy(point(cx, cy, outer, end))} L ${xy(point(cx, cy, inner, end))} A ${inner} ${inner} 0 0 0 ${xy(point(cx, cy, inner, start))} Z`;
}

function ticks(cx, cy, radius, small = false) {
  let out = `<g data-part="compass-ticks">`;
  for (let a = 0; a < 360; a += small ? 5 : 3) {
    const major = a % 30 === 0;
    const medium = a % 15 === 0;
    const p1 = point(cx, cy, radius - (major ? 9 : medium ? 6 : 3), a);
    const p2 = point(cx, cy, radius, a);
    out += `<path d="M ${xy(p1)} L ${xy(p2)}" stroke="${major ? "#75757c" : "#414146"}" stroke-width="${major ? 1.5 : 1}"/>`;
  }
  out += `</g><g data-part="compass-labels" fill="${C.quiet}" font-size="10" font-weight="500" text-anchor="middle" dominant-baseline="middle">`;
  for (let a = 0; a < 360; a += 30) {
    const cardinal = a % 90 === 0;
    const p = point(cx, cy, radius + (small ? 12 : 15), a);
    const label = cardinal
      ? ["N", "E", "S", "W"][a / 90]
      : String(a).padStart(3, "0");
    out += `<text data-bearing="${a}" x="${precision(p[0])}" y="${precision(p[1])}"${cardinal ? ` fill="${a === 0 ? C.coral : "#bebec6"}" font-size="12" font-weight="650"` : ""}>${label}</text>`;
  }
  return `${out}</g>`;
}

function tag(x, y, name, color, part) {
  return `<g transform="translate(${x} ${y})"><path d="M 0 0 L 4 -4 L 8 0 L 4 4 Z" fill="${color}"/><text x="14" y="3" fill="${C.muted}" font-size="10" font-weight="600" letter-spacing="1.3">${name}</text><text data-part="${part}" x="14" y="22" fill="${C.white}" font-size="15" font-weight="550">—</text></g>`;
}

function commonDefs(id) {
  return `<defs>
    <radialGradient id="${id}-face"><stop offset="0" stop-color="#2c2c30"/><stop offset=".68" stop-color="#202023"/><stop offset="1" stop-color="#171719"/></radialGradient>
    <radialGradient id="${id}-fan"><stop offset="0" stop-color="${C.coral}" stop-opacity="0"/><stop offset=".65" stop-color="${C.coral}" stop-opacity=".035"/><stop offset="1" stop-color="${C.coral}" stop-opacity=".29"/></radialGradient>
    <linearGradient id="${id}-coral" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#f1a38d"/><stop offset="1" stop-color="${C.coral}"/></linearGradient>
    <linearGradient id="${id}-sail" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#e5937c" stop-opacity=".35"/><stop offset="1" stop-color="${C.coral}" stop-opacity=".02"/></linearGradient>
    <filter id="${id}-shadow" x="-70%" y="-70%" width="240%" height="240%"><feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity=".5"/></filter>
  </defs>`;
}

function halo(id) {
  const cx = 180;
  const cy = 150;
  return `${commonDefs(id)}
    <g transform="translate(180 141) scale(.9) translate(-180 -150)">
    <circle cx="${cx}" cy="${cy}" r="113" fill="url(#${id}-face)" stroke="#343438"/>
    <circle cx="${cx}" cy="${cy}" r="103" fill="none" stroke="#353539" stroke-width="1"/>
    <circle cx="${cx}" cy="${cy}" r="83" fill="none" stroke="#353539" stroke-width="1"/>
    <path data-part="true-fan" fill="url(#${id}-fan)"/>
    <path data-part="apparent-fan" fill="#fff" fill-opacity=".045"/>
    <path data-part="true-arc" fill="none" stroke="${C.coral}" stroke-opacity=".55" stroke-width="2" stroke-linecap="round"/>
    <path data-part="apparent-arc" fill="none" stroke="${C.white}" stroke-opacity=".45" stroke-width="1.5" stroke-linecap="round"/>
    ${ticks(cx, cy, 119)}
    <path d="M 176 12 L 180 20 L 184 12" fill="none" stroke="${C.white}" stroke-width="1.5" stroke-linejoin="round"/>
    <circle cx="${cx}" cy="${cy}" r="65" fill="#1e1e21" stroke="#3a3a3f" stroke-width=".7"/>
    <text x="${cx}" y="119" text-anchor="middle" fill="${C.muted}" font-size="10" font-weight="600" letter-spacing="2.2">TRUE WIND</text>
    <text data-part="true-speed" x="${cx}" y="164" text-anchor="middle" fill="${C.white}" font-size="44" font-weight="600" letter-spacing="-2">—</text>
    <text x="${cx}" y="184" text-anchor="middle" fill="${C.quiet}" font-size="11" font-weight="500" letter-spacing="1">KNOTS</text>
    <path d="M 180 199 L 175 211 L 180 208 L 185 211 Z" fill="none" stroke="#9b9ba4" stroke-width="1"/>
    <g data-part="true-marker" filter="url(#${id}-shadow)"><path d="M 0 13 L -10 -10 Q 0 -5 10 -10 Z" fill="url(#${id}-coral)"/><path d="M -4 -5 L 0 5 L 4 -5" fill="none" stroke="#ffc9b8" stroke-opacity=".7" stroke-width="1"/></g>
    <g data-part="apparent-marker" filter="url(#${id}-shadow)"><path d="M 0 10 L -7 -8 L 0 -4 L 7 -8 Z" fill="${C.white}"/></g>
    </g>
    ${tag(79, 277, "TRUE", C.coral, "true-angle")}
    ${tag(209, 277, "APPARENT", C.white, "apparent-angle")}
    <path d="M 30 310 H 103 M 257 310 H 330" stroke="#343438"/>
    <text x="180" y="313" text-anchor="middle" fill="${C.quiet}" font-size="7.5" font-weight="500" letter-spacing="1.1">BOW UP · TRUE BEARINGS</text>
    <text x="30" y="330" fill="${C.muted}" font-size="10" letter-spacing="1">AWS <tspan data-part="apparent-speed" fill="${C.white}" font-size="13" font-weight="550">—</tspan><tspan fill="${C.quiet}"> kn</tspan></text>
    <text x="330" y="330" text-anchor="end" fill="${C.muted}" font-size="10" letter-spacing="1">HDG <tspan data-part="heading" fill="${C.white}" font-size="13" font-weight="550">—</tspan><tspan fill="${C.quiet}">°T</tspan></text>`;
}

function sail(id) {
  const cx = 180;
  const cy = 138;
  return `${commonDefs(id)}
    <g transform="translate(180 131) scale(.92) translate(-180 -138)">
    <circle cx="${cx}" cy="${cy}" r="107" fill="url(#${id}-face)"/>
    <circle cx="${cx}" cy="${cy}" r="98" fill="none" stroke="#38383d" stroke-width="10" stroke-opacity=".4"/>
    <circle cx="${cx}" cy="${cy}" r="80" fill="none" stroke="#323237" stroke-width="8" stroke-opacity=".45"/>
    ${ticks(cx, cy, 113)}
    <path data-part="true-ribbon" fill="none" stroke="${C.coral}" stroke-width="10" stroke-linecap="round" stroke-opacity=".55"/>
    <path data-part="apparent-ribbon" fill="none" stroke="${C.white}" stroke-width="7" stroke-linecap="round" stroke-opacity=".24"/>
    <path data-part="true-fan" fill="url(#${id}-fan)"/>
    <path d="M 180 76 V 183" stroke="#c6c6ce" stroke-width="1" stroke-opacity=".55"/>
    <path d="M 176 86 C 163 110 147 128 145 159 L 176 159 Z" fill="url(#${id}-sail)" stroke="${C.coral}" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M 184 96 L 207 155 L 184 155 Z" fill="#f3f3f7" fill-opacity=".065" stroke="#c9c9d2" stroke-width="1" stroke-linejoin="round"/>
    <path d="M 180 64 C 165 106 162 149 166 183 Q 180 197 194 183 C 198 149 195 106 180 64 Z" fill="none" stroke="#b6b6bf" stroke-width="1.2"/>
    <path d="M 180 171 L 180 186 M 168 166 L 192 166" stroke="#73737b" stroke-width="1"/>
    <path data-part="wake-one" d="M 165 205 Q 180 200 195 205" fill="none" stroke="#4f4f55" stroke-width="1.5" stroke-linecap="round"/>
    <path data-part="wake-two" d="M 156 216 Q 180 209 204 216" fill="none" stroke="#36363b" stroke-width="1.3" stroke-linecap="round"/>
    <g data-part="true-marker" filter="url(#${id}-shadow)"><path d="M 0 15 L -12 -9 Q 0 -3 12 -9 Z" fill="url(#${id}-coral)"/><path d="M -6 -4 Q 0 -1 6 -4" fill="none" stroke="#ffe0d5" stroke-opacity=".6"/></g>
    <g data-part="apparent-marker" filter="url(#${id}-shadow)"><path d="M 0 11 L -8 -9 L 0 -5 L 8 -9 Z" fill="${C.white}"/></g>
    <g transform="translate(140 8)"><rect x="0" y="0" width="80" height="25" rx="12.5" fill="#27272b" stroke="#414146"/><text x="40" y="16" text-anchor="middle" fill="${C.white}" font-size="11" font-weight="550"><tspan data-part="heading">—</tspan><tspan fill="${C.quiet}">°T</tspan></text></g>
    <path d="M 180 7 L 176 0 H 184 Z" fill="${C.white}"/>
    </g>
    <path d="M 30 261 H 103 M 257 261 H 330" stroke="#343438"/>
    <text x="180" y="264" text-anchor="middle" fill="${C.quiet}" font-size="7.5" font-weight="500" letter-spacing="1.1">BOW UP · TRUE BEARINGS</text>
    <path d="M 180 279 V 330" stroke="#343438"/>
    <circle cx="41" cy="280" r="3" fill="${C.coral}"/><text x="51" y="284" fill="${C.muted}" font-size="10" font-weight="600" letter-spacing="1.2">TRUE WIND</text>
    <text data-part="true-speed" x="38" y="321" fill="${C.white}" font-size="35" font-weight="600" letter-spacing="-1.6">—</text><text x="119" y="321" fill="${C.quiet}" font-size="11">kn</text>
    <text data-part="true-angle" x="150" y="307" text-anchor="end" fill="${C.coral}" font-size="12" font-weight="550">—</text>
    <circle cx="211" cy="280" r="3" fill="${C.white}"/><text x="221" y="284" fill="${C.muted}" font-size="10" font-weight="600" letter-spacing="1.2">APPARENT</text>
    <text data-part="apparent-speed" x="207" y="321" fill="${C.white}" font-size="35" font-weight="600" letter-spacing="-1.6">—</text><text x="288" y="321" fill="${C.quiet}" font-size="11">kn</text>
    <text data-part="apparent-angle" x="320" y="307" text-anchor="end" fill="#d7d7df" font-size="12" font-weight="550">—</text>`;
}

function tactical(id) {
  const cx = 132;
  const cy = 139;
  return `${commonDefs(id)}
    <circle cx="${cx}" cy="${cy}" r="106" fill="url(#${id}-face)" stroke="#38383d"/>
    <circle cx="${cx}" cy="${cy}" r="88" fill="none" stroke="#36363b" stroke-dasharray="2 6"/>
    <circle cx="${cx}" cy="${cy}" r="69" fill="none" stroke="#343439" stroke-dasharray="2 6"/>
    ${ticks(cx, cy, 106, true)}
    <path d="M 132 28 L 127 18 H 137 Z" fill="${C.white}"/>
    <g data-part="true-trail" fill="none" stroke="${C.coral}" stroke-width="2.3" stroke-linecap="round"/>
    <g data-part="apparent-trail" fill="none" stroke="${C.white}" stroke-width="1.5" stroke-linecap="round"/>
    <path data-part="true-sector" fill="${C.coral}" fill-opacity=".08"/>
    <circle cx="${cx}" cy="${cy}" r="48" fill="#1c1c1f" stroke="#414146" stroke-width=".6"/>
    <text x="${cx}" y="113" text-anchor="middle" fill="${C.muted}" font-size="9" font-weight="600" letter-spacing="1.5">TRUE ANGLE</text>
    <text data-part="true-angle-large" x="${cx}" y="147" text-anchor="middle" fill="${C.white}" font-size="31" font-weight="600" letter-spacing="-1.2">—</text>
    <text data-part="tack" x="${cx}" y="163" text-anchor="middle" fill="${C.coral}" font-size="8" font-weight="600" letter-spacing="1.8">—</text>
    <path d="M 132 173 L 128 183 L 132 180 L 136 183 Z" fill="none" stroke="#a0a0a8" stroke-width="1"/>
    <g data-part="true-marker" filter="url(#${id}-shadow)"><path d="M 0 12 L -9 -10 L 0 -5 L 9 -10 Z" fill="url(#${id}-coral)"/><path d="M -4 -3 L 0 5 L 4 -3" fill="none" stroke="#ffd4c5" stroke-width=".8"/></g>
    <g data-part="apparent-marker"><path d="M 0 9 L -6 -8 L 0 -4 L 6 -8 Z" fill="${C.white}"/></g>
    <path d="M 252 68 V 231" stroke="#3a3a3f"/>
    <path d="M 265 70 L 269 66 L 273 70 L 269 74 Z" fill="${C.coral}"/><text x="279" y="73" fill="${C.muted}" font-size="10" font-weight="600" letter-spacing="1.1">TRUE</text>
    <text data-part="true-speed" x="263" y="111" fill="${C.white}" font-size="34" font-weight="600" letter-spacing="-1.5">—</text><text x="263" y="128" fill="${C.quiet}" font-size="10" letter-spacing="1">KNOTS</text>
    <path d="M 265 166 L 269 162 L 273 166 L 269 170 Z" fill="${C.white}"/><text x="279" y="169" fill="${C.muted}" font-size="10" font-weight="600" letter-spacing="1.1">APPARENT</text>
    <text data-part="apparent-speed" x="263" y="207" fill="${C.white}" font-size="34" font-weight="600" letter-spacing="-1.5">—</text><text x="263" y="224" fill="${C.quiet}" font-size="10" letter-spacing="1">KNOTS</text>
    <path d="M 28 276 H 103 M 257 276 H 334" stroke="#39393e"/>
    <text x="180" y="279" text-anchor="middle" fill="${C.quiet}" font-size="7.5" font-weight="500" letter-spacing="1.1">BOW UP · TRUE BEARINGS</text>
    <text x="28" y="298" fill="${C.muted}" font-size="10" font-weight="600" letter-spacing="1.3">HEADING</text>
    <text x="28" y="328" fill="${C.white}" font-size="26" font-weight="550" letter-spacing="-.8"><tspan data-part="heading">—</tspan><tspan fill="${C.quiet}" font-size="12">°T</tspan></text>
    <text x="215" y="298" fill="${C.muted}" font-size="10" font-weight="600" letter-spacing="1.2">APPARENT ANGLE</text>
    <text data-part="apparent-angle" x="215" y="328" fill="${C.white}" font-size="24" font-weight="550" letter-spacing="-.8">—</text>`;
}

/** Return a complete, standalone SVG. Supply a unique uid for each instance. */
export function createWindDesign(variant, uid) {
  if (!Object.hasOwn(windDesignNames, variant))
    throw new Error(`Unknown wind design: ${String(variant)}`);
  const id = `wind-${String(uid ?? variant).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const content = { halo, sail, tactical }[variant](id);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 340" role="img" aria-label="${windDesignNames[variant]} combined wind instrument" data-wind-design="${variant}" style="display:block;width:100%;height:auto;overflow:visible;font-family:Inter,ui-sans-serif,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-variant-numeric:tabular-nums">${content}</svg>`;
}

function setText(svg, part, text) {
  const node = svg.querySelector(`[data-part="${part}"]`);
  if (node) node.textContent = text;
}

function setAttr(svg, part, attr, value) {
  const node = svg.querySelector(`[data-part="${part}"]`);
  if (node) node.setAttribute(attr, String(value));
}

function updateCompass(svg, cx, cy, radius, hdg, small = false) {
  setAttr(svg, "compass-ticks", "transform", `rotate(${-hdg} ${cx} ${cy})`);
  for (const node of svg.querySelectorAll("[data-bearing]")) {
    const p = point(
      cx,
      cy,
      radius + (small ? 12 : 15),
      Number(node.dataset.bearing) - hdg,
    );
    node.setAttribute("x", precision(p[0]));
    node.setAttribute("y", precision(p[1]));
  }
}

function updateMarker(svg, part, cx, cy, r, a) {
  setAttr(
    svg,
    part,
    "transform",
    `translate(${cx} ${cy}) rotate(${precision(a)}) translate(0 ${-r})`,
  );
}

function trail(svg, part, history, reading, role, cx, cy, radius) {
  const group = svg.querySelector(`[data-part="${part}"]`);
  if (!group) return;
  const samples = history.slice(-16).concat(reading);
  const segments = Math.max(0, samples.length - 1);
  while (group.childElementCount > segments) group.lastElementChild.remove();
  while (group.childElementCount < segments)
    group.appendChild(
      svg.ownerDocument.createElementNS("http://www.w3.org/2000/svg", "path"),
    );
  for (let i = 0; i < segments; i += 1) {
    const a = finite(samples[i]?.[role]);
    const b = finite(samples[i + 1]?.[role]);
    // Keep trail segments on the short side of the circle across the ±180° seam.
    const end = a + relativeAngle(b - a);
    const r = radius - (segments - i - 1) * 0.6;
    group.children[i].setAttribute("d", arc(cx, cy, r, a, end));
    group.children[i].setAttribute(
      "opacity",
      precision(0.08 + (0.55 * (i + 1)) / segments),
    );
  }
}

/**
 * Update an SVG with an interpolated reading. Missing/nonfinite inputs safely
 * use zero; the host should label simulation and handle telemetry quality.
 * history is an optional array of readings, oldest first.
 */
export function updateWindDesign(
  svgElement,
  variant,
  reading = {},
  history = [],
) {
  if (!Object.hasOwn(windDesignNames, variant))
    throw new Error(`Unknown wind design: ${String(variant)}`);
  const h = angle(reading.heading);
  const t = relativeAngle(reading.trueAngle);
  const a = relativeAngle(reading.apparentAngle);
  const tws = speed(reading.trueSpeed);
  const aws = speed(reading.apparentSpeed);
  const samples = Array.isArray(history) ? history : [];
  setText(svgElement, "true-speed", tws);
  setText(svgElement, "apparent-speed", aws);
  setText(svgElement, "heading", heading(h));
  setText(svgElement, "true-angle", signed(t));
  setText(svgElement, "apparent-angle", signed(a));
  svgElement.setAttribute(
    "aria-label",
    `${windDesignNames[variant]}: true wind ${tws} knots, ${signed(t)} relative to bow; apparent wind ${aws} knots, ${signed(a)} relative to bow; heading ${heading(h)} degrees true.`,
  );

  if (variant === "halo") {
    const cx = 180;
    const cy = 150;
    const spread = clamp(
      8 +
        samples
          .slice(-10)
          .reduce(
            (n, p) =>
              Math.max(n, Math.abs(relativeAngle(finite(p?.trueAngle) - t))),
            0,
          ) *
          0.45,
      8,
      22,
    );
    updateCompass(svgElement, cx, cy, 119, h);
    updateMarker(svgElement, "true-marker", cx, cy, 104, t);
    updateMarker(svgElement, "apparent-marker", cx, cy, 84, a);
    setAttr(svgElement, "true-fan", "d", sector(cx, cy, 66, 112, t, spread));
    setAttr(svgElement, "apparent-fan", "d", sector(cx, cy, 66, 91, a, 8));
    setAttr(svgElement, "true-arc", "d", arc(cx, cy, 103, 0, t));
    setAttr(svgElement, "apparent-arc", "d", arc(cx, cy, 83, 0, a));
  } else if (variant === "sail") {
    const cx = 180;
    const cy = 138;
    updateCompass(svgElement, cx, cy, 113, h);
    updateMarker(svgElement, "true-marker", cx, cy, 99, t);
    updateMarker(svgElement, "apparent-marker", cx, cy, 80, a);
    setAttr(svgElement, "true-ribbon", "d", arc(cx, cy, 98, t - 38, t));
    setAttr(svgElement, "apparent-ribbon", "d", arc(cx, cy, 80, a - 30, a));
    setAttr(svgElement, "true-fan", "d", sector(cx, cy, 42, 104, t, 13));
    const sway = Math.sin((t * Math.PI) / 180) * 5;
    const intensity = clamp(finite(reading.trueSpeed) / 20, 0.25, 1);
    setAttr(
      svgElement,
      "wake-one",
      "d",
      `M 165 205 Q ${precision(180 + sway)} ${precision(204 - intensity * 5)} 195 205`,
    );
    setAttr(
      svgElement,
      "wake-two",
      "d",
      `M 156 216 Q ${precision(180 + sway * 1.5)} ${precision(216 - intensity * 7)} 204 216`,
    );
  } else {
    const cx = 132;
    const cy = 139;
    updateCompass(svgElement, cx, cy, 106, h, true);
    updateMarker(svgElement, "true-marker", cx, cy, 89, t);
    updateMarker(svgElement, "apparent-marker", cx, cy, 69, a);
    setText(svgElement, "true-angle-large", `${Math.abs(Math.round(t))}°`);
    setText(
      svgElement,
      "tack",
      t < -0.5 ? "PORT" : t > 0.5 ? "STARBOARD" : "AHEAD",
    );
    setAttr(svgElement, "true-sector", "d", sector(cx, cy, 49, 99, t, 13));
    trail(svgElement, "true-trail", samples, reading, "trueAngle", cx, cy, 88);
    trail(
      svgElement,
      "apparent-trail",
      samples,
      reading,
      "apparentAngle",
      cx,
      cy,
      68,
    );
  }
}
